import { useEffect, useMemo, useState } from 'react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';

function formatDateTime(value) {
  if (!value) return '';
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return '';
  return dt.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function DealNotesTab({ deal, onAddNote }) {
  const sorted = useMemo(() => {
    const notes = Array.isArray(deal?.notes_history) ? deal.notes_history : [];
    return [...notes].sort((a, b) => {
    const at = new Date(a?.created_at || 0).getTime();
    const bt = new Date(b?.created_at || 0).getTime();
    return bt - at;
    });
  }, [deal?.notes_history]);
  const [composerOpen, setComposerOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!composerOpen || saving) return undefined;
    const text = draft.trim();
    if (!text) return undefined;
    const timer = setTimeout(async () => {
      setSaving(true);
      try {
        await onAddNote?.(text);
        setDraft('');
        setComposerOpen(false);
      } catch {
        // Error feedback is emitted by caller.
      } finally {
        setSaving(false);
      }
    }, 900);
    return () => clearTimeout(timer);
  }, [composerOpen, draft, onAddNote, saving]);

  return (
    <div className="min-h-0 flex-1 overflow-y-auto p-4 md:p-6">
      <div className="mx-auto flex w-full max-w-4xl flex-col gap-4">
        <section className="rounded-xl border border-gray-200 bg-white p-4 md:p-5">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-sm font-semibold text-gray-900">Notes</h3>
            {!composerOpen ? (
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-7 px-2.5 text-xs"
                onClick={() => setComposerOpen(true)}
                data-testid="deal-note-add-btn"
              >
                Add Note
              </Button>
            ) : null}
          </div>
          <p className="mt-1 text-xs text-gray-500">
            Notes are timestamped and auto-saved after typing.
          </p>
          {composerOpen ? (
            <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
              <div className="mb-2 text-[11px] text-gray-500">
                {formatDateTime(new Date().toISOString())}
              </div>
              <Textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="Type your note..."
                className="min-h-[88px] bg-white text-sm"
                data-testid="deal-note-input"
                autoFocus
              />
              <div className="mt-2 text-[11px] text-gray-500">
                {saving ? 'Saving...' : 'Auto-save triggers after typing pauses.'}
              </div>
            </div>
          ) : null}
          {sorted.length === 0 ? (
            <div className="mt-4 rounded-md border border-dashed border-gray-300 bg-gray-50 p-4 text-sm text-gray-500">
              No notes yet.
            </div>
          ) : (
            <div className="mt-4 space-y-3">
              {sorted.map((entry) => {
                const createdLabel = formatDateTime(entry.created_at);
                const isRejection = entry.note_type === 'rejection';
                const isManual = entry.note_type === 'manual';
                return (
                  <article
                    key={entry.id}
                    className={`rounded-lg border p-3 ${isRejection ? 'border-purple-200 bg-purple-50/40' : 'border-gray-200 bg-white'}`}
                    data-testid={`deal-note-${entry.id}`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <span
                        className={`inline-flex rounded px-2 py-0.5 text-xs font-semibold ${isRejection ? 'bg-purple-100 text-purple-800' : isManual ? 'bg-sky-100 text-sky-800' : 'bg-gray-100 text-gray-700'}`}
                      >
                        {isRejection ? 'Rejection' : isManual ? 'Note' : entry.note_type || 'Note'}
                      </span>
                      <span className="text-xs text-gray-500">{createdLabel || 'Unknown date'}</span>
                    </div>
                    <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-gray-900">
                      {entry.comment || ''}
                    </p>
                  </article>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
